import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaBook, FaLock, FaPlay, FaCheck, FaGraduationCap, FaArrowLeft, FaCrown } from 'react-icons/fa';
import pathwayTitleMap from './pathwayTitleMap'; // Persian pathway titles
import { API_BASE_URL } from '../config/api';


// Update Lesson interface for rich structure
interface Scene {
  title: string;
  narration: string;
}

interface WordBankEntry {
  word: string;
  translation: string;
}

interface Activity {
  type: string;
  prompt: string;
  options?: string[];
  answer?: string | string[];
  matchPairs?: { left: string; right: string }[];
}

interface Lesson {
  _id?: string;
  id?: string;
  title: string;
  description?: string;
  order: number;
  content?: string;
  isPremium?: boolean;
  category?: string;
  level?: string;
  scenes?: Scene[];
  wordBank?: WordBankEntry[];
  activities?: Activity[];
  type?: string; // Added type for rich-lesson
}

interface Assessment {
  _id: string;
  title: string;
  description?: string;
  order: number;
}

interface Pathway {
  _id: string;
  title: string;
  description: string;
  lessonCount: number;
  assessmentCount?: number;
  category?: string;
  level?: string;
  lessons: Lesson[];
  assessments?: Assessment[];
}

const PathwayDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [pathway, setPathway] = useState<Pathway | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'lessons' | 'assessments'>('lessons');

  // Add debugging logs
  console.log('🔍 PathwayDetail component rendered');
  console.log('🔍 Pathway ID from params:', id);
  console.log('🔍 User state:', user);

  useEffect(() => {
    console.log('🔍 useEffect triggered with id:', id);
    if (id) {
      fetchPathway(id);
    } else {
      console.log('❌ No pathway ID provided');
      setError('No pathway ID provided');
      setLoading(false);
    }
  }, [id]);



  const fetchPathway = async (pathwayId: string) => {
    console.log('🔍 Fetching pathway with ID:', pathwayId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/pathways/${pathwayId}`);
      console.log('🔍 API response status:', response.status);
      
      const data = await response.json();
      console.log('🔍 API response data:', data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch pathway');
      }
      
      setPathway(data.data);
      console.log('✅ Pathway data set successfully');
    } catch (err) {
      console.error('❌ Failed to fetch pathway:', err);
      setError('Failed to load pathway. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getPathwayIcon = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('beginner') || lowerTitle.includes('basic')) return '🎯';
    if (lowerTitle.includes('conversation') || lowerTitle.includes('talk')) return '💬';
    if (lowerTitle.includes('culture') || lowerTitle.includes('about')) return '🏛️';
    if (lowerTitle.includes('survival') || lowerTitle.includes('emergency')) return '🚨';
    if (lowerTitle.includes('reading')) return '📖';
    if (lowerTitle.includes('pronunciation')) return '🎵';
    if (lowerTitle.includes('greeting')) return '👋';
    if (lowerTitle.includes('weather')) return '🌤️';
    if (lowerTitle.includes('hobby')) return '🎨';
    if (lowerTitle.includes('contact')) return '📞';
    if (lowerTitle.includes('minute')) return '⏱️';
    if (lowerTitle.includes('grammar') || lowerTitle.includes('dastoor')) return '📝';
    return '📚';
  };

  // Update gradient and card backgrounds to lavender
  const getPathwayColor = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('beginner') || lowerTitle.includes('basic')) return 'from-purple-200 to-purple-300';
    if (lowerTitle.includes('conversation') || lowerTitle.includes('talk')) return 'from-purple-300 to-purple-400';
    if (lowerTitle.includes('culture') || lowerTitle.includes('about')) return 'from-purple-100 to-purple-200';
    if (lowerTitle.includes('survival') || lowerTitle.includes('emergency')) return 'from-purple-200 to-purple-400';
    if (lowerTitle.includes('reading')) return 'from-purple-100 to-purple-300';
    if (lowerTitle.includes('pronunciation')) return 'from-purple-200 to-purple-400';
    return 'from-purple-100 to-purple-300';
  };

  // Check if user has premium access
  const hasPremiumAccess = () => {
    if (!user) return false;
    return user.subscription.type === 'premium' || user.subscription.type === 'lifetime';
  };

  // Check if lesson is accessible based on subscription
  const isLessonAccessible = (lesson: Lesson, lessonIndex: number) => {
    // Free users get first 3 lessons of each pathway
    if (!hasPremiumAccess()) {
      // For alphabet pathway, allow more lessons since they are fundamental
      if (pathway?.category === 'alphabet' || pathway?.title?.toLowerCase().includes('alphabet')) {
        return lessonIndex < 6; // Allow first 6 alphabet lessons for free users
      }
      return lessonIndex < 3;
    }
    // Premium and lifetime users get all lessons
    return true;
  };

  // Check if lesson has rich content (JSON content with scenes, wordBank, etc.)
  const hasRichContent = (lesson: Lesson) => {
    if (!lesson.content) return false;
    try {
      const parsed = typeof lesson.content === 'string' ? JSON.parse(lesson.content) : lesson.content;
      return (
        parsed &&
        Array.isArray(parsed.scenes) &&
        Array.isArray(parsed.wordBank) &&
        Array.isArray(parsed.activities)
      );
    } catch {
      return false;
    }
  };

  // Get lesson lock status and message
  const getLessonLockInfo = (lesson: Lesson, lessonIndex: number) => {
    if (isLessonAccessible(lesson, lessonIndex)) {
      return { isLocked: false, message: '' };
    }
    
    if (!hasPremiumAccess()) {
      const isAlphabetPathway = pathway?.category === 'alphabet' || pathway?.title?.toLowerCase().includes('alphabet');
      const message = isAlphabetPathway 
        ? 'برای دسترسی به درس‌های بیشتر حروف الفبا، اشتراک پریمیوم تهیه کنید'
        : 'برای دسترسی به این درس، اشتراک پریمیوم تهیه کنید';
      
      return { 
        isLocked: true, 
        message 
      };
    }
    
    return { isLocked: false, message: '' };
  };

  if (loading) {
    return (
      <div className="bg-indigo-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading pathway...</p>
          <p className="text-indigo-200 text-sm mt-2">Pathway ID: {id}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-indigo-900 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-white text-xl font-bold mb-2">Error Loading Pathway</h2>
          <p className="text-indigo-200 mb-4">{error}</p>
          <p className="text-indigo-300 text-sm mb-4">Pathway ID: {id}</p>
          <Link
            to="/pathways"
            className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Pathways
          </Link>
        </div>
      </div>
    );
  }

  if (!pathway) {
    return (
      <div className="bg-indigo-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-yellow-400 text-6xl mb-4">❓</div>
          <h2 className="text-white text-xl font-bold mb-2">Pathway Not Found</h2>
          <p className="text-indigo-200 mb-4">The pathway you're looking for doesn't exist.</p>
          <p className="text-indigo-300 text-sm mb-4">Pathway ID: {id}</p>
          <Link
            to="/pathways"
            className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Pathways
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-indigo-900 min-h-screen">
      {/* Header */}
      <div className="w-full bg-gradient-to-r from-indigo-500 to-indigo-700 py-8 px-4 rounded-b-2xl mb-8 shadow-lg">
        <h1 className="text-3xl font-extrabold mb-4 text-white">{pathway?.title}</h1>
        <p className="text-indigo-100 text-lg">{pathway?.description}</p>
      </div>

      {/* Subscription Banner for Free Users */}
      {!hasPremiumAccess() && (
        <div className="bg-gradient-to-r from-purple-100 to-purple-300 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <div className="text-3xl bg-white/20 rounded-full p-3">
                <FaLock />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1 flex items-center text-black">
                  <FaLock className="mr-2" />
                  Limited Access
                </h3>
                <p className="text-orange-100">
                  {pathway?.category === 'alphabet' || pathway?.title?.toLowerCase().includes('alphabet')
                    ? 'You have access to the first 6 alphabet lessons. Upgrade to premium for access to all lessons and assessments.'
                    : 'You only have access to the first 3 lessons. Upgrade to premium for access to all lessons and assessments.'
                  }
                </p>
              </div>
            </div>
            <Link
              to={user ? "/subscription" : "/register"}
              className="px-6 py-3 bg-white text-orange-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors flex items-center"
            >
              <FaCrown className="mr-2" />
              Upgrade to Premium
            </Link>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <FaBook className="text-blue-600 dark:text-blue-400 text-xl" />
            </div>
            <div className="mr-4">
              <p className="text-sm text-white">Total Lessons</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{pathway?.lessonCount}</p>
            </div>
          </div>
        </div>

        {pathway?.assessmentCount && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                <FaCheck className="text-green-600 dark:text-green-400 text-xl" />
              </div>
              <div className="mr-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Assessments</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{pathway.assessmentCount}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
              <FaGraduationCap className="text-orange-600 dark:text-orange-400 text-xl" />
            </div>
            <div className="mr-4">
              <p className="text-sm text-white">Progress</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">0%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex">
            <button
              onClick={() => setActiveTab('lessons')}
              className={`px-6 py-4 text-sm font-medium transition-colors flex items-center ${
                activeTab === 'lessons'
                  ? 'border-b-2 border-orange-500 text-orange-600 dark:text-orange-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <FaBook className="mr-2" />
              Lessons ({pathway?.lessons.length})
            </button>
            {pathway?.assessments && pathway.assessments.length > 0 && (
              <button
                onClick={() => setActiveTab('assessments')}
                className={`px-6 py-4 text-sm font-medium transition-colors flex items-center ${
                  activeTab === 'assessments'
                    ? 'border-b-2 border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <FaCheck className="mr-2" />
                Assessments ({pathway.assessments.length})
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'lessons' && (
            <div className="space-y-4">
              {pathway?.lessons.map((lesson, index) => {
                const lockInfo = getLessonLockInfo(lesson, index);
                const isAccessible = isLessonAccessible(lesson, index);
                
                return (
                  <div
                    key={lesson._id || lesson.id || index}
                    className="bg-white border border-gray-200 shadow-md rounded-lg p-4 mb-4 transition hover:shadow-lg"
                  >
                    <div className="flex items-center space-x-4 rtl:space-x-reverse">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                        isAccessible 
                          ? 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400' 
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        {lesson.order}
                      </div>
                      <div>
                        <h3 className={`font-semibold flex items-center text-black ${
                          isAccessible 
                            ? '' 
                            : 'opacity-50'
                        }`}>
                          {lesson.title}
                          {/* Indicate rich content lessons visually */}
                          {hasRichContent(lesson) && (
                            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full border border-blue-200">Rich</span>
                          )}
                          {!isAccessible && <FaLock className="ml-2 text-orange-500" />}
                        </h3>
                        {lesson.description && (
                          <p className={`text-sm ${
                            isAccessible 
                              ? 'text-gray-600 dark:text-gray-400' 
                              : 'text-gray-400 dark:text-gray-500'
                          }`} dir="ltr" style={{textAlign: 'left'}}>
                            {lesson.description}
                          </p>
                        )}
                        {!isAccessible && (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            {lockInfo.message}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      {isAccessible ? (
                        <Link
                          to={
                            lesson.type === 'grid-alphabet'
                              ? `/pathways/alphabet-learning-pathway/lesson/${lesson.id || lesson._id}`
                              : lesson.type === 'rich-lesson' || hasRichContent(lesson)
                              ? `/pathways/${pathway._id}/rich-lesson/${lesson.id || lesson._id}`
                              : `/pathways/${pathway._id}/lesson/${lesson.id || lesson._id}`
                          }
                          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm flex items-center"
                        >
                          <FaPlay className="mr-1" />
                          Start
                        </Link>
                      ) : (
                        <Link
                          to={user ? "/subscription" : "/register"}
                          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm flex items-center"
                        >
                          <FaCrown className="mr-1" />
                          Upgrade
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'assessments' && pathway?.assessments && (
            <div className="space-y-4">
              {pathway.assessments.map((assessment, index) => {
                const isAccessible = hasPremiumAccess(); // Assessments are premium-only
                
                return (
                  <div
                    key={assessment._id || index}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-all duration-300 ${
                      isAccessible 
                        ? 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md' 
                        : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center space-x-4 rtl:space-x-reverse">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                        isAccessible 
                          ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' 
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        {assessment.order}
                      </div>
                      <div>
                        <h3 className={`font-semibold flex items-center text-black ${
                          isAccessible 
                            ? '' 
                            : 'opacity-50'
                        }`}>
                          {assessment.title}
                          {!isAccessible && <FaLock className="ml-2 text-orange-500" />}
                        </h3>
                        {assessment.description && (
                          <p className={`text-sm ${
                            isAccessible 
                              ? 'text-gray-600 dark:text-gray-400' 
                              : 'text-gray-400 dark:text-gray-500'
                          }`} dir="ltr" style={{textAlign: 'left'}}>
                            {assessment.description}
                          </p>
                        )}
                        {!isAccessible && (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            Assessments are only available for premium users
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      {isAccessible ? (
                        <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm flex items-center">
                          <FaCheck className="mr-1" />
                          Assess
                        </button>
                      ) : (
                        <Link
                          to={user ? "/subscription" : "/register"}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm flex items-center"
                        >
                          <FaCrown className="mr-1" />
                          Upgrade
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Progress Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          نمای کلی پیشرفت
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-700 dark:text-gray-300">درس‌های تکمیل شده</span>
            <span className="font-semibold text-gray-900 dark:text-white">0 از {pathway?.lessonCount}</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-300"
              style={{ width: '0%' }}
            ></div>
          </div>
          {pathway?.assessments && pathway.assessments.length > 0 && (
            <>
              <div className="flex items-center justify-between mt-6">
                <span className="text-gray-700 dark:text-gray-300">ارزیابی‌های تکمیل شده</span>
                <span className="font-semibold text-gray-900 dark:text-white">0 از {pathway.assessmentCount}</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: '0%' }}
                ></div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Start Learning CTA */}
      <div className="bg-gradient-to-r from-purple-100 to-purple-300 rounded-2xl p-8 text-white text-center">
        <h3 className="text-2xl font-bold mb-4 text-black">
          آماده شروع یادگیری هستید؟ 🚀
        </h3>
        <p className="text-orange-100 mb-6">
          اولین درس را شروع کنید و مسیر یادگیری فارسی خود را آغاز کنید.
        </p>
        {pathway?._id === '1332d56b-fbcd-480d-a56c-7241c74269b5' ? (
          <Link to="/alphabet-learning" className="px-8 py-4 bg-white text-orange-500 font-bold rounded-lg hover:bg-gray-100 transition-colors inline-block">
            شروع اولین درس
          </Link>
        ) : pathway?.lessons && pathway.lessons.length > 0 ? (
          <Link 
            to={`/pathways/${pathway._id}/lesson/${pathway.lessons[0]._id || pathway.lessons[0].id}`} 
            className="px-8 py-4 bg-white text-orange-500 font-bold rounded-lg hover:bg-gray-100 transition-colors inline-block"
          >
            شروع اولین درس
          </Link>
        ) : (
          <button className="px-8 py-4 bg-white text-orange-500 font-bold rounded-lg hover:bg-gray-100 transition-colors">
            شروع اولین درس
          </button>
        )}
      </div>
    </div>
  );
};

export default PathwayDetail; 